import SafeRegexp from 'escape-string-regexp';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { AppConfig } from '../../../core-bundle/config/app-config';
import TextField from '@wetransfer/transfer/component/form/element/textfield';
import TeamDiscoveryExperiment from '@wetransfer/transfer/constant/experiments/team-discovery-experiment';
import Helper from '@wetransfer/transfer/helper';
import { userNeedsTips } from '@wetransfer/transfer/helper/user';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import Actions from '@wetransfer/transfer/store/action';
import { REGEXP_BLANK } from '@wetransfer/transfer/transfer-bundle/config/transfer-config';
import { ErrorBoundary } from '../../ErrorBoundary/ErrorBoundary';

const DEFAULT_SELECTION = 0;
const MAX_SUGGESTIONS = 4;

export class AutoSuggest extends React.Component {
  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.state = {
      value: this.props.defaultValue,
      suggestions: [],
      selectionOffset: 0,
      selected: DEFAULT_SELECTION,
    };

    this.isTabKeyPressed = false;
    this.teamsTooltipTimeout = null;

    this.autoSuggestNode = React.createRef();
    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
  }

  // This is mostly used by the Fields component, to set focus
  // on this when needed (decided by behaviour)
  focus() {
    const { route } = this.props;

    if (!this.inputNode) {
      return;
    }

    if (
      route.type === 'page' ||
      (route.type === 'plus' &&
        window.innerWidth <= AppConfig.panel.full_panel_breakpoint)
    ) {
      return this;
    }

    this.inputNode.focus();
    this.updateScrollPosition();
  }

  updateScrollPosition() {
    // on mobile we don't have the panel prop
    if (!this.props.panel) {
      return;
    }
    this.props.panel.scrollTo(this.autoSuggestNode.current, {
      align: 'center',
    });
  }

  // Update scroll position when we get focus
  onFocus() {
    if (this.props.onFocus) {
      this.props.onFocus();
    }

    if (userNeedsTips(this.props.user)) {
      this.props.pushTransfer({ text: 'TipRecipient' });
    }
  }

  // Every time the value changes
  onChange(e) {
    const cleanValue = e.target.value.replace(/^\s*|\s*$/g, '');
    this.updateScrollPosition();

    if (this.props.onChange) {
      this.props.onChange(cleanValue);
    }
    this.setState({ value: cleanValue });
  }

  // Every time a key is pressed
  onKeyUp(e) {
    if (this.props.onKeyPress) {
      const result = this.props.onKeyPress(e);

      if (result === false) {
        return;
      }
    }

    // Tabbing while in an empty field shouldn't do anything really.
    if (!this.state.value && e.key === 'Tab') {
      return;
    }

    // Hide suggestions because textfield is empty
    // or set the suggestions to whatever matches.
    if (!this.state.value || e.key === 'Escape') {
      this.setState({
        suggestions: [],
        selectionOffset: 0,
        selected: DEFAULT_SELECTION,
      });
    } else {
      const suggestions = this.filterSuggestions();
      let selectionOffset = Math.min(
        suggestions.length - MAX_SUGGESTIONS,
        this.state.selectionOffset
      );

      if (suggestions.length <= MAX_SUGGESTIONS) {
        selectionOffset = 0;
      }

      this.setState({
        suggestions,
        selected:
          suggestions.length === 1
            ? 0
            : Math.min(suggestions.length - 1, this.state.selected),
        selectionOffset,
      });
    }

    // This should 'add' selected item
    let recommendedValue = this.state.value;
    let source = 'typed';

    if (
      (e.key === 'Enter' || e.key === 'Tab') &&
      this.state.suggestions.length &&
      this.state.suggestions[this.state.selected]
    ) {
      recommendedValue = this.state.suggestions[this.state.selected].value;
      source = 'autosuggest';
    }

    // Everything below this is only relevant for Enter or Tab events (Or comma)
    if (e.key !== 'Enter' && e.key !== 'Tab' && e.keyCode !== 188) {
      return;
    }

    // keep the value if the input value doesn't validate, because... IE...
    let newValue = '';

    if (
      !recommendedValue.replace(REGEXP_BLANK, '') ||
      !Helper.validateEmail(recommendedValue)
    ) {
      newValue = recommendedValue;
    }

    // Reset suggestions
    e.preventDefault();
    this.setState({
      value: newValue,
      suggestions: [],
      selected: DEFAULT_SELECTION,
    });

    if (this.props.onRecipient && recommendedValue) {
      this.props.onRecipient(
        recommendedValue,
        source,
        e.key !== 'Tab' ? 'recipientNode' : 'senderNode'
      );
    }

    return false;
  }

  onBlur() {
    if (!this.state.value) {
      return;
    }

    if (this.props.onBlur) {
      this.props.onBlur();
    }

    let newValue = '';

    if (
      this.state.value &&
      this.state.suggestions.length &&
      this.state.suggestions[this.state.selected]
    ) {
      this.props.onRecipient(
        this.state.suggestions[this.state.selected].value,
        'autosuggest',
        false
      );
    } else if (this.state.value) {
      this.props.onRecipient(this.state.value, 'autosuggest', false);
      newValue = Helper.validateEmail(this.state.value) ? '' : this.state.value;
    }

    this.setState({
      value: newValue,
      suggestions: [],
      selectionOffset: 0,
      selected: DEFAULT_SELECTION,
    });
  }

  // On key down we will try to navigate the list of suggestions
  // with our arrow up/down keys
  onKeyDown(e) {
    if (e.key === 'Tab') {
      this.isTabKeyPressed = true;
    }

    // Handle the backspace (delete)
    if (
      e.key === 'Backspace' &&
      this.props.transfer.recipients.length &&
      this.state.value === ''
    ) {
      if (this.props.onDelete) {
        this.props.onDelete();
      }

      return;
    } else if (!this.state.value && e.key === 'Tab') {
      return;
    }

    // Block comma key (see keyup) - it is used to 'add and next'
    if (e.keyCode === 188) {
      e.preventDefault();

      return false;
    }

    if (this.state.suggestions.length === 0) {
      return;
    }
    let selected = false;
    let offset = this.state.selectionOffset;

    // Is this the up or down key?
    if (e.key === 'ArrowDown') {
      offset = Math.min(0, Math.max(offset + 1, 0 - MAX_SUGGESTIONS + 1));
      selected = Math.min(
        this.state.suggestions.length - 1,
        Math.max(0, this.state.selected + 1)
      );
    } else if (e.key === 'ArrowUp') {
      offset = Math.max(offset - 1, 0 - MAX_SUGGESTIONS + 1);
      selected = Math.max(0, this.state.selected - 1);
    } else if (e.key === 'Tab') {
      return this.onKeyUp(e);
    }

    // Only if we selected a suggestion we update the state
    // and cancel the event to stop default behaviour
    if (selected !== false) {
      this.setState({
        selectionOffset: offset,
        selected,
      });
      e.preventDefault();

      return false;
    }
  }

  onSuggestionClick = (index) => {
    // We can't do anything if there are no suggestions
    if (this.state.suggestions.length === 0 || !this.state.suggestions[index]) {
      return;
    }

    // Let's fire the recipient callback and set the selected state
    this.setState({ selected: index });

    if (this.props.onRecipient) {
      this.props.onRecipient(
        this.state.suggestions[index].value,
        'autosuggest'
      );
    }
  };

  filterSuggestions() {
    const { user, contacts } = this.props;
    const { transfer } = this.props;

    // Get previously used addresses from the cookie
    let savedAddresses = Cookie.get('wt_to') || '';
    savedAddresses = savedAddresses.split(/\s*,\s*/).reduce((cur, email) => {
      cur[email] = { name: '' };

      return cur;
    }, {});

    // For Plus users also load the contacts
    if (user.loggedIn && contacts.isLoaded && contacts.list) {
      savedAddresses = { ...savedAddresses, ...contacts.list };
    }

    // Filter over all previously used e-mail addresses.
    const regexp = new RegExp(SafeRegexp(this.state.value), 'gi');

    return Object.keys(savedAddresses).reduce((cur, address) => {
      if (transfer.recipients.indexOf(address) !== -1) {
        return cur;
      }
      const name = savedAddresses[address].name || '';

      if (address.match(regexp) || name.match(regexp)) {
        cur.push({ value: address, label: name });
      }

      return cur;
    }, []);
  }

  renderSuggestions() {
    if (!this.state.suggestions.length) {
      return null;
    }

    // We'll create a React component for every suggestion
    let suggestions = this.state.suggestions.map((option, i) => {
      // We highlight the e-mail part, but not the label
      const email = Helper.highlightText(
        option.value,
        this.state.value,
        'strong'
      );
      let label = null;

      // If we have a label, make it a React element
      if (option.label) {
        label = (
          <span>
            {Helper.highlightText(option.label, this.state.value, 'strong')}
          </span>
        );
      }

      // Return our suggestion element
      return (
        <li
          key={option.value}
          onMouseDown={() => this.onSuggestionClick(i)}
          className={
            'uploader__suggestion' +
            (this.state.selected === i ? ' uploader__suggestion--selected' : '')
          }
        >
          {email}
          {label}
        </li>
      );
    });

    // Calculate which suggestions we will display
    const offset = Math.max(
      0,
      this.state.selected - this.state.selectionOffset - MAX_SUGGESTIONS + 1
    );

    // Now cut out the suggestions we want to display
    suggestions = suggestions.slice(offset, offset + MAX_SUGGESTIONS);

    return <ul>{suggestions}</ul>;
  }

  componentDidMount() {
    const { user, contacts } = this.props;

    if (user.loggedIn && !contacts.isLoaded && !contacts.isPending) {
      this.props.listContacts();
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.inputNode) {
      return;
    }

    if (this.props.recipientCount > prevProps.recipientCount) {
      this.inputNode.value = '';
    }
  }

  render() {
    const textfieldProps = {
      type: 'email',
      key: 'autosuggest-textfield',
      name: 'autosuggest',
      hideLabelOnInput: true,
      className: !Browser.isMobile ? 'textfield--borderbottom' : null,
      label: this.props.placeholder,
      disabled: this.props.disabled,
      autoComplete: 'off',
      autoCorrect: 'off',
      autoCapitalize: 'off',
      spellCheck: 'false',
      fieldRef: (node) => (this.inputNode = node),
      value: this.state.value,
      onChange: this.onChange,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      onKeyUp: this.onKeyUp,
      onKeyDown: this.onKeyDown,
      describedBy: this.props.describedBy,
    };

    if (this.props.autoFocus) {
      textfieldProps.shouldFocus = true;
    }

    return (
      <div ref={this.autoSuggestNode} className="uploader__autosuggest">
        <TextField {...textfieldProps} />
        <ErrorBoundary
          componentName="autosuggest"
          logMessage="There was an error rendering suggestions"
        >
          {this.renderSuggestions()}
        </ErrorBoundary>
      </div>
    );
  }
}

AutoSuggest.propTypes = {
  autoFocus: PropTypes.bool,
  route: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  contacts: PropTypes.object.isRequired,
  pushTransfer: PropTypes.func.isRequired,
  clearTransfer: PropTypes.func.isRequired,
  listContacts: PropTypes.func.isRequired,
  transfer: PropTypes.object.isRequired,
  panel: PropTypes.object,
  defaultValue: PropTypes.string,
  placeholder: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
  onRecipient: PropTypes.func,
  onKeyPress: PropTypes.func,
  recipientCount: PropTypes.number,
  disabled: PropTypes.bool,
  describedBy: PropTypes.string,
};

AutoSuggest.defaultProps = {
  defaultValue: '',
};

const mapStateToProps = ({ route, user, contacts }) => ({
  route,
  user,
  contacts,
});

export const mapDispatchToProps = {
  pushTransfer: Actions.Notification.pushTransfer,
  listContacts: Actions.Contacts.list,
  clearTransfer: Actions.Notification.clearTransfer,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(AutoSuggest);
